import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// interfaces.
import { ExceptionRule, IncentiveStep } from "@pages/RulesPage/modules/RulesMain/rulesData";
import { RawRuleItem } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

interface RulesState {
  exceptions: ExceptionRule[];
  incentiveStepsCO: IncentiveStep[];
  incentiveStepsMX: IncentiveStep[];
  rawRulesCO: RawRuleItem[];
  rawRulesMX: RawRuleItem[];
  isSaving: boolean;
}

const initialState: RulesState = {
  exceptions: [],
  incentiveStepsCO: [],
  incentiveStepsMX: [],
  rawRulesCO: [],
  rawRulesMX: [],
  isSaving: false,
};

export const rulesSlice = createSlice({
  initialState,
  name: "rules",
  reducers: {
    setIsSaving(state, action: PayloadAction<boolean>) {
      state.isSaving = action.payload;
    },
    setRulesForCountry(
      state,
      action: PayloadAction<{
        country: string;
        exceptions: ExceptionRule[];
        steps: IncentiveStep[];
        rawRules: RawRuleItem[];
      }>,
    ) {
      const { country, exceptions, steps, rawRules } = action.payload;
      state.exceptions = exceptions;
      if (country === "MX") {
        state.incentiveStepsMX = steps;
        state.rawRulesMX = rawRules;
      } else {
        state.incentiveStepsCO = steps;
        state.rawRulesCO = rawRules;
      }
    },
    updateException(state, action: PayloadAction<ExceptionRule>) {
      state.exceptions = state.exceptions.map((exc) =>
        exc.id === action.payload.id ? action.payload : exc,
      );
    },
    updateIncentiveStep(
      state,
      action: PayloadAction<{ country: string; step: IncentiveStep }>,
    ) {
      const { country, step } = action.payload;
      if (country === "MX") {
        state.incentiveStepsMX = state.incentiveStepsMX.map((s) =>
          s.id === step.id ? step : s,
        );
      } else {
        state.incentiveStepsCO = state.incentiveStepsCO.map((s) =>
          s.id === step.id ? step : s,
        );
      }
    },
    updateRawRule(
      state,
      action: PayloadAction<{ country: string; rawRule: RawRuleItem }>,
    ) {
      const { country, rawRule } = action.payload;
      const list = country === "MX" ? state.rawRulesMX : state.rawRulesCO;
      const idx = list.findIndex((r) => r.rule_id === rawRule.rule_id);
      if (idx !== -1) list[idx] = rawRule;
    },
    addRawRule(
      state,
      action: PayloadAction<{ country: string; rawRule: RawRuleItem }>,
    ) {
      const { country, rawRule } = action.payload;
      if (country === "MX") {
        state.rawRulesMX.push(rawRule);
      } else {
        state.rawRulesCO.push(rawRule);
      }
    },
  },
});

export const {
  setIsSaving,
  setRulesForCountry,
  updateException,
  updateIncentiveStep,
  updateRawRule,
  addRawRule,
} = rulesSlice.actions;

export default rulesSlice.reducer;
